import {useEffect, useState} from 'react'
import axios from 'axios'
import {toast} from 'sonner'
import {PageTitle} from '../../../_metronic/layout/core'
import {useAuth} from '../../modules/auth'
import {config} from '../../config'

type PosVendor = 'square' | 'clover'
const ALL_VENDORS: PosVendor[] = ['square', 'clover']
const vendorLabel = (v: PosVendor) => (v === 'square' ? 'Square' : 'Clover')

// ─── Types ──────────────────────────────────────────────────────────────────

interface PosStatus {
  connected: boolean
  hasLocation: boolean
  posVendor?: string
  posMerchantId?: string
  posLocationId?: string
  connectedAt?: string
}

interface SquareLocation {
  id: string
  name: string
  address?: {address_line_1?: string; locality?: string; administrative_district_level_1?: string; postal_code?: string}
  status?: string
}

interface LocationMatchResult {
  locationId: string
  locationName: string
  score: number
  address: SquareLocation['address'] | null
}

// ─── Component ──────────────────────────────────────────────────────────────

const ConnectPOS = () => {
  const {currentUser} = useAuth()
  const BASE_URL = config.apiUrl

  const [status, setStatus] = useState<PosStatus | null>(null)
  const [selectedVendor, setSelectedVendor] = useState<PosVendor>('square')
  const [activeVendor, setActiveVendor] = useState<PosVendor>('square')
  const [locations, setLocations] = useState<SquareLocation[]>([])
  const [matchScores, setMatchScores] = useState<Record<string, number>>({})
  const [restaurantAddress, setRestaurantAddress] = useState<{street?: string; city?: string; state?: string} | null>(null)
  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [loadingConnect, setLoadingConnect] = useState(false)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [autoMatching, setAutoMatching] = useState(false)
  const [savingLocation, setSavingLocation] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  const authHeaders = {Authorization: `Bearer ${currentUser?.api_token}`}
  const vendorForCalls: PosVendor = (status?.posVendor as PosVendor | undefined) ?? activeVendor

  // ── Fetch current connection status for whichever vendor is connected ─────
  const fetchStatus = async (preferred?: PosVendor) => {
    setLoadingStatus(true)
    const order: PosVendor[] = preferred
      ? [preferred, ...ALL_VENDORS.filter((v) => v !== preferred)]
      : [...ALL_VENDORS]
    try {
      for (const vendor of order) {
        try {
          const {data} = await axios.get(`${BASE_URL}/pos/${vendor}/me/status`, {headers: authHeaders})
          const s = data.data ?? {connected: false, hasLocation: false}
          if (s.connected) {
            setStatus(s)
            setActiveVendor(vendor)
            setSelectedVendor(vendor)
            return
          }
        } catch {
          // Try next vendor
        }
      }
      setStatus({connected: false, hasLocation: false})
      setActiveVendor(preferred ?? selectedVendor)
    } finally {
      setLoadingStatus(false)
    }
  }

  // ── Auto-match: compare restaurant address to Square locations ───────────
  const runAutoMatch = async () => {
    setAutoMatching(true)
    try {
      const {data} = await axios.post(`${BASE_URL}/pos/${vendorForCalls}/me/auto-match-location`, {}, {headers: authHeaders})
      const result = data.data
      // Store address used for matching so the UI can show it
      if (result.restaurantAddress) setRestaurantAddress(result.restaurantAddress)
      if (result.autoMatched) {
        toast.success(`Auto-matched to "${result.location.locationName}" (score ${result.location.score}/6)`)
        fetchStatus()
        return
      }
      // Not auto-matched — populate the manual picker with scores
      const ranked: LocationMatchResult[] = result.ranked ?? []
      const locs: SquareLocation[] = ranked.map((r) => ({
        id: r.locationId,
        name: r.locationName,
        address: r.address ?? undefined,
        status: 'ACTIVE',
      }))
      const scores: Record<string, number> = {}
      ranked.forEach((r) => { scores[r.locationId] = r.score })
      setLocations(locs)
      setMatchScores(scores)
      if (ranked[0]) setSelectedLocationId(ranked[0].locationId) // pre-select best
      if (ranked.length > 0) {
        toast.info(`No confident auto-match found. Best candidate: "${ranked[0].locationName}" (score ${ranked[0].score}/6). Please confirm.`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Auto-match failed')
      fetchLocations()
    } finally {
      setAutoMatching(false)
    }
  }

  // ── Fetch Square locations for location picker (manual fallback) ──────────
  const fetchLocations = async () => {
    setLoadingLocations(true)
    try {
      const {data} = await axios.get(`${BASE_URL}/pos/${vendorForCalls}/me/locations`, {headers: authHeaders})
      setLocations((data.data?.locations as SquareLocation[]) ?? [])
      setMatchScores({})
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not load locations')
    } finally {
      setLoadingLocations(false)
    }
  }

  // ── Handle OAuth callback params on page load ─────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthStatus = params.get('status')
    const oauthVendor = (params.get('vendor') as PosVendor | null) ?? selectedVendor
    const message = params.get('message')

    if (oauthStatus === 'connected') {
      setSelectedVendor(oauthVendor)
      setActiveVendor(oauthVendor)
      toast.success(`${vendorLabel(oauthVendor)} connected! Now pick your location.`)
    } else if (oauthStatus === 'clover-install-complete') {
      setSelectedVendor('clover')
      setActiveVendor('clover')
      toast.info(
        'Clover install finished on the merchant. Click Connect Clover here to finish OAuth.',
        {duration: 9000}
      )
    } else if (oauthStatus === 'error') {
      toast.error(message ? decodeURIComponent(message) : 'OAuth failed. Please try again.')
    }

    // Clean the URL
    if (oauthStatus) {
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    fetchStatus(oauthVendor)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── When connected but no location yet, try auto-match first ─────────────
  useEffect(() => {
    if (status?.connected && !status.hasLocation) {
      runAutoMatch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // ── Kick off Square OAuth ─────────────────────────────────────────────────
  const handleConnect = async () => {
    setLoadingConnect(true)
    try {
      const {data} = await axios.get(`${BASE_URL}/pos/${selectedVendor}/oauth/connect`, {headers: authHeaders})
      const authorizeUrl = data.data?.authorizeUrl
      if (authorizeUrl) {
        window.location.href = authorizeUrl
      } else {
        toast.error(`Could not get ${vendorLabel(selectedVendor)} authorization URL`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to start ${vendorLabel(selectedVendor)} connection`)
    } finally {
      setLoadingConnect(false)
    }
  }

  // ── Save chosen location ──────────────────────────────────────────────────
  const handleSelectLocation = async () => {
    if (!selectedLocationId) {
      toast.error('Please select a location')
      return
    }
    setSavingLocation(true)
    try {
      await axios.post(
        `${BASE_URL}/pos/${vendorForCalls}/me/select-location`,
        {locationId: selectedLocationId},
        {headers: authHeaders}
      )
      toast.success('Location saved!')
      fetchStatus()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save location')
    } finally {
      setSavingLocation(false)
    }
  }

  // ── Disconnect + revoke OAuth token from Square ───────────────────────────
  const handleDisconnect = async (thenReconnect = false) => {
    setDisconnecting(true)
    setShowDisconnectConfirm(false)
    try {
      await axios.delete(`${BASE_URL}/pos/${vendorForCalls}/me/disconnect`, {headers: authHeaders})
      toast.success(`${vendorLabel(vendorForCalls)} access revoked and disconnected.`)
    } catch {
      // Still clear local state even if the server call fails
      toast.warning('Could not reach server, cleared local state.')
    } finally {
      setDisconnecting(false)
    }
    if (thenReconnect) {
      handleConnect()
    } else {
      setStatus({connected: false, hasLocation: false})
      setActiveVendor(selectedVendor)
      setLocations([])
      setSelectedLocationId('')
      setMatchScores({})
      setRestaurantAddress(null)
    }
  }

  const handleReconnect = () => handleDisconnect(true)

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loadingStatus) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Connect POS</PageTitle>
        <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading…</span>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Connect POS</PageTitle>


      {/* ── State 1: Not connected ───────────────────────────────────────── */}
      {!status?.connected && (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-header border-0 pt-6'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>Point-of-Sale Integration</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Connect your Square or Clover account to sync orders with DIIBS
              </span>
            </h3>
          </div>
          <div className='card-body d-flex flex-column align-items-center justify-content-center text-center py-10'>
            <div
              className='border rounded-3 p-8 d-flex flex-column align-items-center gap-4'
              style={{maxWidth: 320, width: '100%'}}
            >
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-primary'
                style={{width: 72, height: 72, fontSize: 32}}
              >
                ◼
              </div>
              <div>
                <h4 className='fw-bold mb-1'>{vendorLabel(selectedVendor)}</h4>
                <p className='text-muted fs-7 mb-0'>
                  Connect your POS merchant account to pull locations, catalog, and orders.
                </p>
              </div>
              <div className='w-100 text-start'>
                <label className='form-label fw-semibold'>POS vendor</label>
                <select
                  className='form-select'
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value as PosVendor)}
                  disabled={loadingConnect}
                >
                  <option value='square'>Square</option>
                  <option value='clover'>Clover</option>
                </select>
              </div>
              <button
                className='btn btn-primary w-100'
                onClick={handleConnect}
                disabled={loadingConnect}
              >
                {loadingConnect ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' />
                    Redirecting to {vendorLabel(selectedVendor)}…
                  </>
                ) : (
                  `Connect ${vendorLabel(selectedVendor)}`
                )}
              </button>
            </div>

            <p className='text-muted fs-8 mt-6' style={{maxWidth: 400}}>
              You will be redirected to your selected POS vendor to authorize DIIBS. After approving, you will be
              brought back here to select your location.
            </p>
          </div>
        </div>
      )}

      {/* ── State 2: Connected, pick a location ─────────────────────────── */}
      {status?.connected && !status.hasLocation && (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-header border-0 pt-6 d-flex align-items-center justify-content-between flex-wrap gap-3'>
            <h3 className='card-title align-items-start flex-column mb-0'>
              <span className='card-label fw-bold fs-3 mb-1'>Select Your {vendorLabel(vendorForCalls)} Location</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Merchant: <span className='font-monospace'>{status.posMerchantId ?? '—'}</span>
                {restaurantAddress && (
                  <span className='ms-3 text-muted'>
                    · Matching against: <em>{[restaurantAddress.street, restaurantAddress.city, restaurantAddress.state].filter(Boolean).join(', ')}</em>
                  </span>
                )}
              </span>
            </h3>
            <div className='d-flex gap-2 flex-wrap'>
              <button
                className='btn btn-sm btn-light-primary'
                onClick={() => handleDisconnect(true)}
                disabled={loadingConnect}
              >
                {loadingConnect ? (
                  <><span className='spinner-border spinner-border-sm me-1' role='status' />Redirecting…</>
                ) : (
                  '← Connect Different Account'
                )}
              </button>
              <button
                className='btn btn-sm btn-light-danger'
                onClick={() => handleDisconnect(false)}
              >
                ✕ Disconnect
              </button>
            </div>
          </div>
          <div className='card-body'>
            {(autoMatching || loadingLocations) ? (
              <div className='d-flex flex-column align-items-center py-10 gap-3'>
                <div className='spinner-border text-primary' role='status' />
                <span className='text-muted fs-7'>{autoMatching ? `Auto-matching your restaurant address to a ${vendorLabel(vendorForCalls)} location…` : 'Loading locations…'}</span>
              </div>
            ) : locations.length === 0 ? (
              <div className='alert alert-warning d-flex align-items-start gap-3'>
                <span className='fs-4'>⚠</span>
                <div>
                  <strong>No locations found</strong> for this {vendorLabel(vendorForCalls)} account.
                  <br />
                  Make sure your account has at least one location, or{' '}
                  <button
                    className='btn btn-sm btn-warning ms-1'
                    onClick={() => handleDisconnect(true)}
                  >
                    connect a different account
                  </button>
                </div>
              </div>
            ) : (
              <div className='row g-4'>
                {locations.map((loc) => {
                  const isSelected = selectedLocationId === loc.id
                  return (
                    <div className='col-md-4' key={loc.id}>
                      <div
                        className={`card border h-100 ${isSelected ? 'border-primary bg-light-primary' : 'border-dashed'}`}
                        onClick={() => setSelectedLocationId(loc.id)}
                        style={{cursor: 'pointer'}}
                      >
                        <div className='card-body d-flex flex-column gap-2'>
                          <div className='d-flex align-items-center justify-content-between gap-2'>
                            <div className='d-flex align-items-center gap-2'>
                              <div
                                className={`w-16px h-16px rounded-circle border border-2 flex-shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-secondary'}`}
                              />
                              <span className='fw-bold fs-6'>{loc.name}</span>
                            </div>
                            {matchScores[loc.id] !== undefined && (
                              <span
                                className={`badge ${
                                  matchScores[loc.id] >= 4 ? 'badge-success' :
                                  matchScores[loc.id] >= 2 ? 'badge-warning' :
                                  'badge-light-secondary'
                                } fs-8`}
                                title='Address match score (0–6)'
                              >
                                {matchScores[loc.id] >= 4 ? '✓ Best match' :
                                 matchScores[loc.id] >= 2 ? '~ Partial' :
                                 '✗ Low'}
                              </span>
                            )}
                          </div>
                          {loc.address?.address_line_1 && (
                            <span className='text-muted fs-7'>
                              {loc.address.address_line_1}
                              {loc.address.locality ? `, ${loc.address.locality}` : ''}
                              {loc.address.administrative_district_level_1
                                ? ` ${loc.address.administrative_district_level_1}`
                                : ''}
                            </span>
                          )}
                          <span
                            className={`badge ${loc.status === 'ACTIVE' ? 'badge-light-success' : 'badge-light-warning'} align-self-start`}
                          >
                            {loc.status ?? 'UNKNOWN'}
                          </span>
                          <span className='text-muted fs-8'>ID: {loc.id}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {locations.length > 0 && (
              <div className='mt-8 d-flex gap-3 flex-wrap align-items-center'>
                <button
                  className='btn btn-primary'
                  onClick={handleSelectLocation}
                  disabled={!selectedLocationId || savingLocation}
                >
                  {savingLocation ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' />
                      Saving…
                    </>
                  ) : (
                    'Confirm Location'
                  )}
                </button>
                <button className='btn btn-light-primary' onClick={runAutoMatch} disabled={autoMatching}>
                  {autoMatching ? 'Matching…' : '⚡ Re-run Auto-Match'}
                </button>
                <button className='btn btn-light' onClick={fetchLocations} disabled={loadingLocations}>
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── State 3: Fully connected ─────────────────────────────────────── */}
      {status?.connected && status.hasLocation && (
        <>
          <div className='card mb-5 mb-xl-10'>
            <div className='card-body d-flex flex-column flex-md-row align-items-center gap-6 py-10'>
              {/* Icon */}
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-success flex-shrink-0'
                style={{width: 80, height: 80, fontSize: 36}}
              >
                ✓
              </div>
              <div className='flex-grow-1'>
                <h3 className='fw-bold mb-1 text-success'>{vendorLabel(vendorForCalls)} Connected</h3>
                <div className='row g-3 mt-2'>
                  <div className='col-sm-6'>
                    <span className='text-muted fs-7 d-block'>Vendor</span>
                    <span className='fw-semibold fs-6 text-capitalize'>{status.posVendor}</span>
                  </div>
                  <div className='col-sm-6'>
                    <span className='text-muted fs-7 d-block'>Merchant ID</span>
                    <span className='fw-semibold fs-6 font-monospace'>{status.posMerchantId ?? '—'}</span>
                  </div>
                  <div className='col-sm-6'>
                    <span className='text-muted fs-7 d-block'>Active Location ID</span>
                    <span className='fw-semibold fs-6 font-monospace'>{status.posLocationId ?? '—'}</span>
                  </div>
                  <div className='col-sm-6'>
                    <span className='text-muted fs-7 d-block'>Connected</span>
                    <span className='fw-semibold fs-6'>
                      {status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : '—'}
                    </span>
                  </div>
                </div>
              </div>
              <div className='d-flex flex-column gap-2 min-w-150px'>
                <button className='btn btn-sm btn-light-primary' onClick={handleReconnect} disabled={disconnecting}>
                  ↺ Switch Account
                </button>
                <button
                  className='btn btn-sm btn-light'
                  disabled={disconnecting}
                  onClick={() => {
                    if (status) setStatus({...status, hasLocation: false})
                    runAutoMatch()
                  }}
                >
                  ⚡ Change Location
                </button>

                {/* Revoke access ─────────────────────────── */}
                {!showDisconnectConfirm ? (
                  <button
                    className='btn btn-sm btn-light-danger mt-2'
                    disabled={disconnecting}
                    onClick={() => setShowDisconnectConfirm(true)}
                  >
                    {disconnecting ? (
                      <><span className='spinner-border spinner-border-sm me-1' role='status' />Revoking…</>
                    ) : (
                      '✕ Revoke & Disconnect'
                    )}
                  </button>
                ) : (
                  <div className='border border-danger rounded p-3 mt-2 d-flex flex-column gap-2 bg-light-danger'>
                    <span className='fs-8 fw-bold text-danger'>Revoke {vendorLabel(vendorForCalls)} OAuth access?</span>
                    <span className='fs-9 text-muted'>This removes DIIBS from your {vendorLabel(vendorForCalls)} app authorizations and deletes the stored token.</span>
                    <div className='d-flex gap-2'>
                      <button
                        className='btn btn-sm btn-danger flex-grow-1'
                        onClick={() => handleDisconnect(false)}
                        disabled={disconnecting}
                      >
                        {disconnecting ? <span className='spinner-border spinner-border-sm' role='status' /> : 'Yes, Revoke'}
                      </button>
                      <button
                        className='btn btn-sm btn-light flex-grow-1'
                        onClick={() => setShowDisconnectConfirm(false)}
                        disabled={disconnecting}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick test panel ─────────────────────────────────────────────── */}
          <QuickTestPanel
            baseUrl={BASE_URL!}
            authHeaders={authHeaders}
            locationId={status.posLocationId!}
            vendor={vendorForCalls}
          />
        </>
      )}
    </>
  )
}

// ─── Quick Test Panel ────────────────────────────────────────────────────────
// Lets you pull locations / catalog / orders right from the UI for a quick sanity check.

interface QuickTestPanelProps {
  baseUrl: string
  authHeaders: Record<string, string>
  locationId: string
  vendor: PosVendor
}

type TestTab = 'locations' | 'catalog' | 'orders' | 'all'

const QuickTestPanel = ({baseUrl, authHeaders, locationId, vendor}: QuickTestPanelProps) => {
  const [activeTab, setActiveTab] = useState<TestTab>('locations')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<any>(null)

  const endpoints: Record<TestTab, string> = {
    locations: `${baseUrl}/pos/${vendor}/me/locations`,
    catalog: `${baseUrl}/pos/${vendor}/me/catalog`,
    orders: `${baseUrl}/pos/${vendor}/me/orders?location_ids=${locationId}&limit=5`,
    all: `${baseUrl}/pos/${vendor}/me/all`,
  }

  const runTest = async () => {
    setLoading(true)
    setResult(null)
    try {
      const {data} = await axios.get(endpoints[activeTab], {headers: authHeaders})
      setResult(data.data)
    } catch (err: any) {
      setResult({error: err?.response?.data?.message || err?.message || 'Request failed'})
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Test Your Connection</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            Pull live data from your connected {vendorLabel(vendor)} account
          </span>
        </h3>
      </div>
      <div className='card-body'>
        {/* Tab selector */}
        <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
          {(['locations', 'catalog', 'orders', 'all'] as TestTab[]).map((tab) => (
            <li className='nav-item' key={tab}>
              <button
                className={`nav-link text-capitalize ${activeTab === tab ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab(tab)
                  setResult(null)
                }}
              >
                {tab}
              </button>
            </li>
          ))}
        </ul>

        <button className='btn btn-primary mb-4' onClick={runTest} disabled={loading}>
          {loading ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' />
              Fetching…
            </>
          ) : (
            `Fetch ${activeTab}`
          )}
        </button>

        {result && (
          <div className='bg-light rounded p-4' style={{maxHeight: 400, overflow: 'auto'}}>
            <pre className='mb-0 fs-8' style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}

export default ConnectPOS
